import { BehaviorSubject, defer, repeat } from 'rxjs'
import { inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

import { EditorMarker, LocateResult, MediaFile, MediaFileBundle, Tag } from 'elements-sdk'
import { APIService } from 'api'
import { Logger, LoggerFactory } from 'common/log'
import { MessageBox, MessageBoxService } from 'common/util'
import { NotifyService } from 'common/util/services/notify.service'
import { ElementsClientService } from 'common/util.elementsClient'
import { OperatingSystemService } from 'common/util/services/operatingSystem.service'
import { ClientSideURLService } from 'media/services/clientSideURL.service'
import { Time } from 'media/time'
import { GenericFileReader } from 'uploader/services/uploader.service'

export async function loadAssetContentsPages (
  api: APIService,
  dir: MediaFile,
  callback: (bundles: MediaFileBundle[], files: MediaFile[]) => Promise<any>,
): Promise<any> {
  let offset = 0
  let total = 1

  while (offset < total) {
    const contents = await api.mediaLibrary.getMediaFileContents({
      id: dir.id,
      offset,
      limit: 50,
      includeModifiedBy: true,
      includeUnrecognized: false,
      includeDeleted: false,
    })
    offset += 50
    total = contents.total

    const bundles = contents.bundles.filter(x => {
      return x.asset
        && ['video', 'audio', 'image'].includes(x.asset.type!)
        && x.mainfile.present
    })

    await callback(bundles, contents.directories)
  }
}

export abstract class BaseInterface {
  uri = true
  native = false
  hasProxy = true

  readonly active$ = new BehaviorSubject<boolean>(false)

  get active (): boolean {
    return this.active$.value
  }

  set active (value: boolean) {
    if (value !== this.active$.value) {
      this.active$.next(value)
    }
  }

  private _busyCount = 0
  readonly busy$ = new BehaviorSubject<boolean>(false)

  isBusy (): boolean {
    return this._busyCount > 0
  }

  /**
   * Run an async operation while marking the panel as busy.
   * Use this to wrap long-running operations like import or sync.
   */
  async withBusyState<T> (fn: () => Promise<T>): Promise<T> {
    this._busyCount++
    if (this._busyCount === 1) {
      this.busy$.next(true)
    }
    try {
      return await fn()
    } finally {
      this._busyCount = Math.max(0, this._busyCount - 1)
      if (this._busyCount === 0) {
        this.busy$.next(false)
      }
    }
  }

  canToggleProxyMode = true
  canUploadSequence = false
  platform: string
  app: string
  currentMediaRootID: number | undefined
  systemPath: Record<string, string>

  readonly activeSequenceTimestamp$ = defer(() => this.getActiveSequenceTimestamp()).pipe(repeat({ delay: 500 }))

  logger: Logger
  protected api: APIService
  protected clientSideUrl: ClientSideURLService
  protected notify: NotifyService
  protected elementsClient: ElementsClientService
  protected translate: TranslateService
  protected messageBox: MessageBoxService
  protected operatingSystem: OperatingSystemService

  constructor () {
    this.logger = inject(LoggerFactory).create('BaseInterface')
    this.api = inject(APIService)
    this.clientSideUrl = inject(ClientSideURLService)
    this.notify = inject(NotifyService)
    this.elementsClient = inject(ElementsClientService)
    this.translate = inject(TranslateService)
    this.messageBox = inject(MessageBoxService)
    this.operatingSystem = inject(OperatingSystemService)
  }

  abstract init (): Promise<void>

  abstract getFS (): any

  abstract getSystemPath (name: string): string

  abstract getApp (): string

  abstract getPlatform (): string

  abstract restart ()

  abstract getActiveSequenceID (): Promise<string | null>

  abstract getActiveSequenceTimestamp (): Promise<Time | null>

  abstract setActiveSequenceTimestamp (t: Time): Promise<void>

  abstract getActiveSequencePaths (): Promise<string[]>

  abstract getProjectPaths (): Promise<string[]>

  abstract isProxyMode (): Promise<boolean>

  abstract setProxyMode (enabled)

  abstract attachProxies (paths: Record<string, string>)

  abstract importDirs (dirs, folder?): Promise<any>

  abstract importPath (path: string)

  abstract open (item: MediaFileBundle)

  abstract getLocalPath (options)

  abstract hasConfig (): boolean

  abstract setConfig (key, value)

  abstract getConfig (key)

  abstract hasRestart (): boolean

  importPaths (paths) {
    for (const path of paths) {
      this.importPath(path)
    }
  }

  async locateItem (locateEndpointRequest): Promise<LocateResult> {
    const response = await this.api.private.locateFile({ locateEndpointRequest })
    if (!response.workspace || !response.path) {
      this.logger.warn('Asset is outside of workspaces')
      throw new Error('Asset is outside of workspaces')
    }
    return response
  }

  async importAsset (asset) {
    this.logger.info('Importing asset:', asset)

    const response = await this.locateItem({ asset: asset.id })
    await this.mountWorkspaces([response.workspace])

    const exp = await this.api.mediaLibrary.editorExportXMLForAsset({ assetIds: asset.id.toString(), uri: this.uri })
    await this.importXML(exp)
  }

  async querySubclipImport (bundles) {
    const assetIds = bundles.map(item => item.asset.id)
    if (!assetIds?.length) {
      return []
    }

    let importSubclips = true
    const root = this.currentMediaRootID
    const subclips = (await Promise.all(assetIds.map(assetId => this.api.mediaLibrary.getAllSubclips({ assetId, root }).catch(() => [])))).flat()

    if (subclips.length) {
      const text = this.translate.instant(`Import {count, plural,
        =1{one additional subclip?}
        other{{count} additional subclips?} }`, { count: subclips.length })

      try {
        await this.messageBox.show({
          text,
          positive: this.translate.instant('Yes'),
          negative: this.translate.instant('No'),
        })
      } catch {
        importSubclips = false
        this.logger.info('Not importing subclips')
      }
    }

    return importSubclips ? subclips : []
  }

  async importBundles (bundles) {
    this.logger.info('Importing bundles:', bundles)
    if (!bundles.length) {
      return
    }

    const response = await this.locateItem({ path: bundles[0].mainfile.fullPath })
    await this.mountWorkspaces([response.workspace])

    const exp = await this.api.mediaLibrary.editorExportXMLForBundle({ bundleIds: bundles.map(x => x.id).join(','), uri: this.uri })
    await this.importXML(exp.contents)
  }

  async importEditorProject (fileId: number) {
    this.logger.info('Importing project:', fileId)

    try {
      const response = await this.api.mediaLibrary.locateEditorProjectPaths({ id: fileId })
      await this.mountWorkspaces(new Set(response.map(x => x.workspace).filter(x => x)))
    } catch (error) {
      this.logger.warn('Failed to mount workspaces:', error)
    }

    const exp = await this.api.mediaLibrary.editorExportXMLForProject({ id: fileId, uri: this.uri })
    await this.importXML(exp.contents)
  }

  async mountWorkspaces (workspaceIds) {
    this.logger.debug('Automounting workspaces:', ...workspaceIds)
    let progress: MessageBox | null = null

    try {
      await this.elementsClient.refresh()
      if (!this.elementsClient.connected) {
        throw new Error('Client not connected')
      }
      for (const workspaceId of workspaceIds) {
        const isMounted = await this.elementsClient.isMounted(workspaceId)
        if (!isMounted) {
          progress = this.messageBox.show({ progress: true, title: this.translate.instant('Mounting the workspace') })
          progress.catch(() => null)
          await this.elementsClient.mount(workspaceId)
        }
      }
    } catch (error) {
      this.logger.error('Automount failed:', error)
    } finally {
      if (progress) progress.close()
    }
  }

  async importXML (xml) {
    this.logger.debug('Importing XML content')

    const fs = this.getFS()
    const path = this.getSystemPath(this.systemPath.USER_DATA!) + '/.elements-temp.xml'

    try {
      await fs.writeFile(path, xml)
      await this.importPath(path)
    } catch (error) {
      this.reportError('Import failed', error)
    }
  }

  async importFile (path) {
    this.logger.info('Importing file:', path)

    const response = await this.locateItem({ path })
    await this.mountWorkspaces([response.workspace])

    const url = await this.clientSideUrl.getPathUrl(path, this.platform)
    return this.importPath(url)
  }

  async getMarkersForPath (_path: string): Promise<EditorMarker[]> {
    return []
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async replaceMetadataForPath (_path: string, _markers: EditorMarker[], _newTags: Tag[]): Promise<void> { }

  async renderSequence (
    _ready: (_: GenericFileReader) => Promise<void>,
  ): Promise<void> {
    throw new Error('Not supported')
  }

  isRenderingSequence (): boolean {
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openURL (_url: string): void { }

  reportError (label: string, error: unknown): void {
    this.logger.error(`${label}:`, error)
    const message = error instanceof Error ? error.message : String(error)
    this.notify.error(label, message)
  }
}
